import {
    HtmlNewsHeadlineRecord,
    isHtmlHeadline,
    isPdfHeadline,
    isTextHeadline,
    NewsHeadlineRecord,
    NewsHeadlineRecords,
    PdfNewsHeadlineRecord,
    TextNewsHeadlineRecord
} from "./NewsHeadlineRecord";
import {NewsItemType} from "../types/NewsItemType";
import {NewsItemIds} from "../types/NewsItemId";
import {isNotNull} from "../../js/util/UtilFunctions";
import {ArrayUtil} from "../../js/util/ArrayUtil";
import {NewsSource, newsVendorSource} from "../types/NewsVendors";
import {OffsetDateTime} from "../../pseudo-types/DateTypes";
import moment from "moment";
import {Transform} from "../../types/GenericTypes";
import {Moment} from "moment-timezone";

export type NewsHeadlineDataRow = NewsHeadlineDateSeparator | NewsHeadlineViewModel;

export type NewsHeadlineDataRows = ReadonlyArray<NewsHeadlineDataRow>;

export interface NewsHeadlineDateSeparator {
    readonly date: string;
}

function dateSeparator(date: string): NewsHeadlineDateSeparator {
    return {date};
}

export function isDateSeparator(row: NewsHeadlineDataRow): row is NewsHeadlineDateSeparator {
    return "date" in row && !("source" in row);
}

export function isHeadlineModel(row: NewsHeadlineDataRow): row is NewsHeadlineViewModel {
    return !("date" in row) && "source" in row;
}

export type NewsHeadlineViewModels = ReadonlyArray<NewsHeadlineViewModel>;

export interface NewsHeadlineViewModel extends NewsHeadlineRecord {
    readonly read: boolean;
    readonly source: NewsSource;
}

export interface TextNewsHeadlineViewModel extends NewsHeadlineViewModel {
    readonly type: NewsItemType.TEXT;
}

export function isTextModel(model: NewsHeadlineViewModel): model is TextNewsHeadlineViewModel {
    return model.type === NewsItemType.TEXT;
}

function textViewModel(record: TextNewsHeadlineRecord, source: NewsSource): TextNewsHeadlineViewModel {
    return {
        ...record,
        read: true,
        source
    };
}

export interface HtmlNewsHeadlineViewModel extends NewsHeadlineViewModel {
    readonly type: NewsItemType.HTML;
}

export function isHtmlModel(model: NewsHeadlineViewModel): model is HtmlNewsHeadlineViewModel {
    return model.type === NewsItemType.HTML;
}

function htmlViewModel(record: HtmlNewsHeadlineRecord, read: boolean, source: NewsSource): HtmlNewsHeadlineViewModel {
    return {
        ...record,
        read,
        source
    };
}

export interface PdfNewsHeadlineViewModel extends NewsHeadlineViewModel {
    readonly type: NewsItemType.DOCUMENT;
    readonly url: string;
}

export function isDocumentModel(model: NewsHeadlineViewModel): model is PdfNewsHeadlineViewModel {
    return model.type === NewsItemType.DOCUMENT;
}

function pdfViewModel(record: PdfNewsHeadlineRecord,
                      read: boolean,
                      source: NewsSource,
                      sessionId: string | null): PdfNewsHeadlineViewModel {
    const url = updateUrl(record.url, sessionId);
    return {
        ...record,
        url,
        read,
        source
    };
}

const sessionIdUpdatePattern = /(.*[?&]uid=)(\w+)(&?.*)/;

function updateUrl(url: string, sessionId: string | null): string {
    if (sessionId == null) {
        return url;
    } else {
        return url.replace(sessionIdUpdatePattern, `$1${sessionId}$3`);
    }
}

export function BuildModels(headlines: NewsHeadlineRecords,
                            readItems: NewsItemIds,
                            sessionId: string | null): NewsHeadlineViewModels {
    const models = headlines.map((h) => buildModel(h, readItems, sessionId))
                            .filter(isNotNull)
                            .sort((a, b) => b.at.localeCompare(a.at));
    return ArrayUtil.distinctBy(models, (m) => m.id);
}

function buildModel(headline: NewsHeadlineRecord,
                    readItems: NewsItemIds,
                    sessionId: string | null): NewsHeadlineViewModel | null {
    const read = readItems.includes(headline.id);
    const source = newsVendorSource(headline.vendor);

    if (isTextHeadline(headline)) {
        return textViewModel(headline, source);
    } else if (isHtmlHeadline(headline)) {
        return htmlViewModel(headline, read, source);
    } else if (isPdfHeadline(headline)) {
        return pdfViewModel(headline, read, source, sessionId);
    } else {
        console.warn("Unable to handle News Item of type " + headline.type);
        return null;
    }
}

export function addDateSeparators(headlines: NewsHeadlineViewModels): NewsHeadlineDataRows {
    // Can't do anything with an empty list
    if (headlines.length === 0) {
        return headlines;
    }

    const first = headlines[0];
    const dateDisplayFn = relativeDateDisplay(moment());
    const firstDate = dateDisplayFn(first.at);
    const result: Array<NewsHeadlineDataRow> = [dateSeparator(firstDate)];
    let currentDate = firstDate;
    headlines.forEach((h) => {
        const headlineDate = dateDisplayFn(h.at);
        if (currentDate !== headlineDate) {
            result.push(dateSeparator(headlineDate));
        }
        result.push(h);
        currentDate = headlineDate;
    });

    return result;
}

function relativeDateDisplay(now: Moment): Transform<OffsetDateTime, string> {
    return (asAt) => {
        const at = moment(asAt);
        if (at.isSame(now, "day")) {
            return "Today";
        } else if (now.diff(at, "day") === 1) {
            return "Yesterday";
        } else {
            return at.format("DD MMMM YYYY");
        }
    };
}
